import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Clock, MapPin, ArrowRight, HelpCircle } from "lucide-react";
import Layout from "../components/Layout";
import RevealText from "../components/RevealText";
import { whatsappLink, brandPhoneLink } from "../lib/whatsapp";

const PHONE = "(800) 715-6516";
const EMAIL = "support@velcorenergy.com";

export default function Support() {
  const { t } = useTranslation();

  return (
    <Layout>
      <Helmet>
        <title>{t("supportPage.metaTitle")} — Velcor Energy</title>
        <meta name="description" content={t("supportPage.metaDesc")} />
      </Helmet>

      <section className="relative bg-velcor-cream pt-12 md:pt-20 pb-12 md:pb-16" data-testid="support-hero">
        <div className="max-w-[980px] mx-auto px-5 text-center">
          <span className="inline-block text-velcor-green mb-3" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {t("supportPage.kicker")}
          </span>
          <h1 className="display text-velcor-ink leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 56px)" }}>
            <RevealText text={t("supportPage.heroPre")} />{" "}
            <RevealText text={t("supportPage.heroAccent")} className="display-accent" delay={0.2} />
          </h1>
        </div>
      </section>

      {/* Channels grid */}
      <section className="bg-white py-12 md:py-16" data-testid="support-channels">
        <div className="max-w-[1080px] mx-auto px-5 grid md:grid-cols-3 gap-4">
          <ChannelCard
            icon={MessageCircle}
            title={t("supportPage.waTitle")}
            body={t("supportPage.waBody")}
            cta={t("supportPage.waCta")}
            href={whatsappLink()}
            external
            highlight
            testid="support-wa"
          />
          <ChannelCard
            icon={Phone}
            title={t("supportPage.phoneTitle")}
            body={t("supportPage.phoneBody")}
            cta={PHONE}
            href={brandPhoneLink()}
            testid="support-phone"
          />
          <ChannelCard
            icon={Mail}
            title={t("supportPage.emailTitle")}
            body={t("supportPage.emailBody")}
            cta={EMAIL}
            href={`mailto:${EMAIL}`}
            testid="support-email"
          />
        </div>
      </section>

      {/* Hours + address */}
      <section className="bg-slate-50 py-16" data-testid="support-info">
        <div className="max-w-[920px] mx-auto px-5 grid md:grid-cols-2 gap-6">
          <InfoBlock icon={Clock} title={t("supportPage.hoursTitle")}>
            <ul className="space-y-1 text-[14px] text-slate-600">
              <li><strong className="text-velcor-ink">Lun – Vie:</strong> 8:00 am – 7:00 pm CT</li>
              <li><strong className="text-velcor-ink">Sábado:</strong> 9:00 am – 1:30 pm CT</li>
              <li><strong className="text-velcor-ink">Domingo:</strong> {t("supportPage.closed")}</li>
              <li className="text-slate-400 italic text-[12.5px] pt-2">{t("supportPage.hoursNote")}</li>
            </ul>
          </InfoBlock>
          <InfoBlock icon={MapPin} title={t("supportPage.locationTitle")}>
            <address className="not-italic text-[14px] text-slate-600 leading-relaxed">
              Velcor Energy<br />
              440 Louisiana St, Ste 1200<br />
              Houston, TX 77002-1063<br />
              United States
            </address>
          </InfoBlock>
        </div>
      </section>

      {/* FAQ link */}
      <section className="bg-white py-16 text-center">
        <div className="max-w-[680px] mx-auto px-5">
          <HelpCircle className="w-7 h-7 text-velcor-green mx-auto mb-4" strokeWidth={1.6} />
          <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(24px, 2.8vw, 32px)" }}>
            {t("supportPage.faqTitle")}{" "}
            <span className="display-accent">{t("supportPage.faqTitleAccent")}</span>
          </h2>
          <p className="mt-3 text-slate-500" style={{ fontSize: 14.5 }}>{t("supportPage.faqBody")}</p>
          <Link
            to="/faq"
            className="mt-6 inline-flex items-center gap-2 text-velcor-ink font-semibold border-b border-velcor-ink/30 hover:border-velcor-green hover:text-velcor-green pb-1 transition-colors text-[14.5px]"
            data-testid="support-faq-link"
          >
            {t("supportPage.faqCta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function ChannelCard({ icon: Icon, title, body, cta, href, external, highlight, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      className={`group relative block rounded-2xl p-6 border transition-all duration-300 ${
        highlight
          ? "bg-velcor-green text-white border-velcor-green hover:bg-velcor-green/95 shadow-[0_18px_36px_rgba(47,122,58,0.28)]"
          : "bg-white border-slate-200 hover:border-velcor-green hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)]"
      }`}
    >
      <Icon className={`w-5 h-5 mb-4 ${highlight ? "text-white" : "text-velcor-green"}`} strokeWidth={2} />
      <h3 className={`font-black tracking-tight mb-2 ${highlight ? "text-white" : "text-velcor-ink"}`} style={{ fontSize: 18 }}>{title}</h3>
      <p className={`leading-relaxed mb-4 ${highlight ? "text-white/85" : "text-slate-500"}`} style={{ fontSize: 14 }}>{body}</p>
      <span className={`inline-flex items-center gap-1.5 font-extrabold ${highlight ? "text-white" : "text-velcor-green"}`} style={{ fontSize: 13.5 }}>
        {cta}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </span>
    </a>
  );
}

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-velcor-green/10 text-velcor-green">
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <h2 className="font-black text-velcor-ink tracking-tight" style={{ fontSize: 17 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}
